/* eslint-disable no-underscore-dangle */

const _ = require('lodash');

// This is done to make it easier to use this inside the class
const classes = {
  tenant: 'tenant',
  document: 'document',
  order: 'order',
  product: 'product',
  pack: 'pack',
};

const types = {
  listOfValues: 'listOfValues',
  listOfRanges: 'listOfRanges',
  formula: 'formula',
};

class Attribute {
  /**
   * @param {Object} config The configuration to build the attribute with
   * @param {string} config.key the attribute key
   * @param {string} config.type the attribute type enum.
   * @param {string} config.class the attribute class enum.
   * @param {boolean} config.required true if and only if the attribute is required for white list validation.
   * @param {string} config.description A description for the attribute.
   * @param {string} config.default A default for the attribute.
   * @param {string} config.negate If true, return the specified valid options, otherwise return all options other than those specified
   */
  constructor(config) {
    if (_.isEmpty(config.key)) {
      throw new Error('key is required');
    }

    if (!_.has(config, 'required')) {
      throw new Error('required is required');
    }

    if (!classes[config.class]) {
      throw new Error(`Invalid class ${config.class} please use one of: ${_.keys(classes).join(', ')}`);
    }

    this._key = config.key;
    this._required = config.required;
    this._class = config.class;
    this._subclass = config.subclass;
    this._derived = config.derived;
    // Renaming property internally for clarity of code. Matches name on UI.
    this._isValid = config.negate;
  }

  /**
   * Determines if the attribute can be implied. Attributes are implied
   * after the engine is run if they have only one valid value left in their selection.
   * Some attribute cannot support this feature, others can. This is called inside the engine
   * before we attempt to imply this attribute.
   *
   * @return {bool} true if and only if this attribute can be implied
   */
  // eslint-disable-next-line class-methods-use-this
  canBeImplied() {
    throw new Error('Not implemented');
  }

  get key() {
    return this._key;
  }

  get required() {
    return this._required;
  }

  get evaluateState() {
    return this._evaluateState;
  }

  get class() {
    return this._class;
  }

  get subclass() {
    return this._subclass;
  }

  get isValid() {
    return this._isValid;
  }

  get derived() {
    return this._derived;
  }

  /**
   * Is the given domain a subset of this attribute's values
   * @param  {Object} attributeDomain The attribute domain to evaluate.
   * @return {bool} true if and only if the given domain is a subset of this attribute's valid values
   */
  isSubset(attributeDomain) {
    const relevantValues = attributeDomain[this._key.toLowerCase()];

    return _.every(relevantValues, value => this.contains(value));
  }

  /**
   * Is the given domain a disjoint set of this attribute's values
   * @param  {Object} attributeDomain The attribute domain to evaluate.
   * @return {bool} true if and only if the given domain is a disjoint set of this attribute's valid values
   */
  isDisjointSet(attributeDomain) {
    const relevantValues = attributeDomain[this._key.toLowerCase()];

    return _.every(relevantValues, value => !this.overlaps(value));
  }

  /**
   * Evaluates the current rule based on the provided attributeDomain.
   *
   * @param  {Object} attributeDomain The attribute selection to evaluate.
   * @return {bool} true if and only if this attribute is valid through the evaluation of attributeDomain.
   */
  evaluate(attributeDomain) {
    const relevantSelections = attributeDomain[this._key.toLowerCase()];
    if (_.isNil(relevantSelections) || _.isEmpty(relevantSelections)) {
      return false;
    }

    return this.isValid ? this.isDisjointSet(attributeDomain) : this.isSubset(attributeDomain);
  }
}

Attribute.Classes = classes;
Attribute.Types = types;

module.exports = Attribute;
